import dynamic from 'next/dynamic';
import {ProductDetails} from '@components/product/global/ProductDetails';
import {Layout} from '@components/layout/index';
import {ProductProvider} from '@components/product/ProductProvider';
import {GlobalExecute} from '@components/datalayer/global';
import {finalizeProductPrices} from '@helpers/config-master.mjs';
import {SendViewItemHitToAnalyticsAPI} from '@components/facebook/view-item-hit-fb';
import {DesktopMaxWidth, DesktopSpinner} from '@components/widgets/Common';
import {ViewItem_GA4} from '@components/datalayer/google';
import {getEligibleProductPaths} from '@helpers/sitemap/get-product-paths.mjs';
import {logDuringNextProductionBuild} from '@helpers/common/logging.mjs';
import {consoleLogRed, consoleLogRedWhite} from '@helpers/common/console-log-colors.mjs';
import {getProductByHandle} from '@helpers/products/get-by-handle/db-queries.mjs';

const BatteryProductPage = dynamic(() =>
  import('@components/product/ProductPageTemplates/BatteryProductPage').then(
    (mod) => mod.BatteryProductPage
  )
);

const SoundDeadeningCalculator = dynamic(() =>
  import('@components/product/global/SoundDeadeningCalculator').then(
    (mod) => mod.SoundDeadeningCalculator
  )
);

const SingleProductFocusWithAssets = dynamic(() =>
  import('@components/product/ProductPageTemplates/SingleProductFocusWithAssets').then(
    (mod) => mod.SingleProductFocusWithAssets
  )
);

export const getStaticPaths = async () => {
  const paths = await getEligibleProductPaths();
  logDuringNextProductionBuild(paths, {enabled: false});

  const output = {
    paths: paths,
    fallback: 'blocking'
  };
  return output;
};

export const getStaticProps = async (context) => {
  // console.dir({context}, {depth: 3});

  try {
    const productHandle = context?.params?.handle?.[0];
    // consoleLogBabyBlueOrange('[...handle][getStaticProps] context?.params:', context?.params);

    const output = await getProductByHandle(productHandle, context);
    // console.dir({output}, {depth: 3});
    // stop();

    const outputHasProduct =
      !!output?.product?.id || !!output?.normalizedProducts?.parentVariant?.sku;
    const enabledOnSite =
      output?.product?.isEnabledOnSite ||
      output?.normalizedProducts?.parentVariant?.isEnabledOnSite;

    if (!outputHasProduct) {
      consoleLogRed(` [Error][${productHandle}] Product not found on site`);
      return {
        notFound: true
      };
    }

    if (enabledOnSite !== true) {
      consoleLogRed(` [Warning][${productHandle}] Product not enabled on site`);
      return {
        notFound: true
      };
    }

    // console.dir({getSingleProductInfo_output: output}, {depth: 2});
    // console.log('\n >> Product Page Total File Size: ', JSON.stringify(output)?.length / 1024, 'Kb');

    return {
      props: output,
      revalidate: 1800 // Every 30 minutes
    };
  } catch (e) {
    consoleLogRedWhite('[Error][getStaticProps] Message:', e?.message);
    consoleLogRedWhite('[Error][getStaticProps] Stack:', e?.stack?.split('\n'));
    return {
      notFound: true
    };
  }
};

export default function ProductHandle(props) {
  const reviewsFromDb = props?.reviewsFromDb;
  const product = props?.product || props?.normalizedProducts?.parentVariant;
  // console.dir({ProductHandle_product: product}, {depth: 3});

  const category = product?.category || props?.category;

  const isBatteries = category === 'batteries';
  const isSoundDeadening = category === 'sound-deadening';

  const [productWithFinalPrice] = finalizeProductPrices([product]);
  // console.dir({productWithFinalPrice}, {depth: 3});

  const fireViewItemEvents = product?.id;

  if (!product?.id) {
    return (
      <DesktopMaxWidth>
        <DesktopSpinner/>
      </DesktopMaxWidth>
    );
  }

  if (isBatteries) {
    return (
      <ProductProvider data={productWithFinalPrice} reviewsFromDb={reviewsFromDb}>
        <BatteryProductPage {...props} />
      </ProductProvider>
    );
  }

  if (isSoundDeadening) {
    const normalizedProducts = props?.normalizedProducts ?? {};
    const alternativeVariantSelector = {
      MainTitle: 'Kits',
      AlternateTitle: 'By Vehicle Type',
      component: <SoundDeadeningCalculator normalizedProducts={normalizedProducts}/>
    };

    return (
      <SingleProductFocusWithAssets
        {...props}
        alternativeVariantSelector={alternativeVariantSelector}
      />
    );
  }

  return (
    <>
      <ProductProvider data={productWithFinalPrice} reviewsFromDb={reviewsFromDb}>
        <div className="">
          <ProductDetails reviewsFromDb={reviewsFromDb}/>

          {fireViewItemEvents && (
            <GlobalExecute
              execute={() => {
                ViewItem_GA4(product);
                SendViewItemHitToAnalyticsAPI(product); // handles Skar/FB view_item event server side
              }}
            />
          )}
        </div>
      </ProductProvider>
    </>
  );
}

ProductHandle.Layout = Layout;

